import React, { useMemo, useState } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';
import { lensTokens } from '~/configs/unifiedAnalyticsTheme';
import { type NativePrintProps, NativeReportShell } from '~/nativeLenses/NativePrintBoard';
import { nativeLensFailure } from '~/nativeLenses/nativeLensFailure';
import { nativeLensId } from '~/nativeLenses/nativeLensId';
import { useNativePrintMode } from '~/nativeLenses/nativePrintSession';

import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  Divider,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { FONT_STACK_SUISSE_INTL } from '~/components/brand/brandTokens';

import PageChatButton from '~/pages/AskRichardUnified/page/PageChatButton';
import { usePageBridge } from '~/pages/AskRichardUnified/page/pageBridge';
import type { PageField } from '~/pages/AskRichardUnified/page/pageContract';
import { validatePageFilters } from '~/pages/AskRichardUnified/page/pageFilters';

import { useGetReportingLeLensData } from '~/hooks/apis/reporting/useGetReportingLeLensData';

import {
  CARD_ID,
  type Fact,
  INSIGHTS_VIEW,
  type InsightsScope,
  type Multi,
  PAGE_ID,
  RESULT_MEASURES,
  type ResultRow,
  SUMMARY_VIEW,
  type SourceBar,
  type SummaryScope,
  VIEWS,
  type WeekBar,
  continuousColor,
  distinctValues,
  formatCurrency,
  formatDate,
  formatInteger,
  formatPercent,
  measureText,
  readFacts,
  resultCsv,
  resultTotal,
  sameMembers,
  sessionsSum,
  sourceBars,
  sourceCsv,
  weekCsv,
  weeklyBars,
  weeklyResults,
} from './liveDataAdapter';
import { download } from './liveModel';
import { SAVED_SOURCE_MEMBERS } from './sourceMembers';
import { resetViewFilters } from './viewFilters';

const SAVED = new Set<string>(SAVED_SOURCE_MEMBERS);
const PAGE_SIZE = 40;
const SCHEMA: Record<string, PageField> = {
  view: { type: 'enum', values: [...VIEWS] },
  from: { type: 'text', maxLength: 10 },
  to: { type: 'text', maxLength: 10 },
  insightsFrom: { type: 'text', maxLength: 10 },
  insightsTo: { type: 'text', maxLength: 10 },
  source: { type: 'text', maxLength: 8000 },
  campaign: { type: 'text', maxLength: 8000 },
  country: { type: 'text', maxLength: 500 },
  state: { type: 'text', maxLength: 500 },
  insightsSource: { type: 'text', maxLength: 8000 },
  insightsCampaign: { type: 'text', maxLength: 8000 },
  action: { type: 'text', maxLength: 300 },
};

function readDate(params: URLSearchParams, key: string): string | null {
  const value = params.get(key);
  return value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : null;
}

function readMulti(params: URLSearchParams, key: string): Multi {
  if (!params.has(key)) return 'all';
  const values = params.getAll(key);
  if (values.length === 1 && values[0] === '__ALL__') return 'all';
  if (values.length === 1 && values[0] === '__NONE__') return [];
  return values;
}

function readSources(params: URLSearchParams, key: string): 'saved' | Multi {
  if (!params.has(key)) return 'saved';
  const values = params.getAll(key);
  if (values.length === 1 && values[0] === '__ALL__') return 'all';
  if (values.length === 1 && values[0] === '__SAVED__') return 'saved';
  if (values.length === 1 && values[0] === '__NONE__') return [];
  return values;
}

function writeMulti(params: URLSearchParams, key: string, value: Multi) {
  params.delete(key);
  if (value === 'all') return;
  if (value.length === 0) params.append(key, '__NONE__');
  else value.forEach((item) => params.append(key, item));
}

function writeSources(params: URLSearchParams, key: string, value: 'saved' | Multi) {
  params.delete(key);
  if (value === 'saved') return;
  if (value === 'all') params.append(key, '__ALL__');
  else if (value.length === 0) params.append(key, '__NONE__');
  else value.forEach((item) => params.append(key, item));
}

function choiceLabel(checked: readonly string[], options: readonly string[]): string {
  if (checked.length === 0) return '(None)';
  if (options.length > 0 && checked.length === options.length) return '(All)';
  if (checked.length === 1) return checked[0] === '' ? '(blank)' : checked[0];
  return '(Multiple values)';
}

function checkedOf(value: 'saved' | Multi, options: readonly string[]): string[] {
  if (value === 'all') return [...options];
  if (value === 'saved') return options.filter((option) => SAVED.has(option));
  return value.filter((option) => options.includes(option));
}

function settleSources(checked: readonly string[], options: readonly string[]): 'saved' | Multi {
  if (options.length > 0 && sameMembers(checked, options)) return 'all';
  const savedVisible = options.filter((option) => SAVED.has(option));
  const extras = checked.filter((option) => !SAVED.has(option));
  const missing = savedVisible.filter((option) => !checked.includes(option));
  if (extras.length === 0 && missing.length === 0) return 'saved';
  return [...checked];
}

function settleMulti(checked: readonly string[], options: readonly string[]): Multi {
  return options.length > 0 && sameMembers(checked, options) ? 'all' : [...checked];
}

function extent(facts: readonly Fact[]): { min: string; max: string } {
  let min = '';
  let max = '';
  facts.forEach((fact) => {
    if (!min || fact.campaignDate < min) min = fact.campaignDate;
    if (!max || fact.campaignDate > max) max = fact.campaignDate;
  });
  return { min, max };
}

function Sparkline({ facts }: { facts: readonly Fact[] }) {
  const counts = new Map<string, number>();
  facts.forEach((fact) => counts.set(fact.campaignWeek, (counts.get(fact.campaignWeek) ?? 0) + fact.sessions));
  const bars = [...counts.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  const peak = Math.max(1, ...bars.map((bar) => bar[1]));
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-end', height: 28, gap: '1px', mb: 0.5 }}>
      {bars.map(([week, count]) => (
        <Box
          key={week}
          sx={{
            flex: 1,
            minWidth: 0,
            height: `${Math.max(8, (count / peak) * 100)}%`,
            bgcolor: (theme) => lensTokens(theme).accent,
            opacity: 0.5,
          }}
        />
      ))}
    </Box>
  );
}

function DateRange({
  from,
  to,
  min,
  max,
  facts,
  onChange,
}: {
  from: string | null;
  to: string | null;
  min: string;
  max: string;
  facts: readonly Fact[];
  onChange: (from: string | null, to: string | null) => void;
}) {
  return (
    <Box>
      <Typography sx={{ fontSize: 12, fontWeight: 700 }}>Campaign Date</Typography>
      <Sparkline facts={facts} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        <TextField
          label="Campaign Date from"
          size="small"
          type="date"
          value={from ?? min}
          InputLabelProps={{ shrink: true }}
          inputProps={{ min: min || undefined, max: max || undefined }}
          onChange={(event) => onChange(event.target.value || null, to)}
        />
        <TextField
          label="Campaign Date to"
          size="small"
          type="date"
          value={to ?? max}
          InputLabelProps={{ shrink: true }}
          inputProps={{ min: min || undefined, max: max || undefined }}
          onChange={(event) => onChange(from, event.target.value || null)}
        />
      </Box>
    </Box>
  );
}

function CheckFilter({
  label,
  options,
  checked,
  onApply,
}: {
  label: string;
  options: readonly string[];
  checked: readonly string[];
  onApply: (values: string[]) => void;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [draft, setDraft] = useState<string[]>([...checked]);
  const [query, setQuery] = useState('');
  const visible = options.filter((option) => option.toLowerCase().includes(query.trim().toLowerCase())).slice(0, 250);
  const toggle = (option: string) =>
    setDraft((current) =>
      current.includes(option) ? current.filter((item) => item !== option) : [...current, option],
    );
  return (
    <Box>
      <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{label}</Typography>
      <Button
        size="small"
        variant="outlined"
        sx={{
          textTransform: 'none',
          justifyContent: 'space-between',
          width: '100%',
          fontSize: 12,
          color: 'text.primary',
        }}
        onClick={(event) => {
          setDraft([...checked]);
          setQuery('');
          setAnchor(event.currentTarget);
        }}
      >
        {choiceLabel(checked, options)}
      </Button>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 1, width: 280 }}>
          <TextField
            size="small"
            label={`${label} search`}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          <Box sx={{ display: 'flex', gap: 1, my: 0.5 }}>
            <Button size="small" onClick={() => setDraft([...options])}>
              All
            </Button>
            <Button size="small" onClick={() => setDraft([])}>
              None
            </Button>
          </Box>
          <Box sx={{ maxHeight: 240, overflow: 'auto' }}>
            {visible.map((option) => (
              <label key={option || '(blank)'} style={{ display: 'flex', gap: 6, fontSize: 12 }}>
                <input type="checkbox" checked={draft.includes(option)} onChange={() => toggle(option)} />
                <span>{option === '' ? '(blank)' : option}</span>
              </label>
            ))}
          </Box>
          <Button
            size="small"
            variant="contained"
            sx={{ mt: 1, textTransform: 'none' }}
            onClick={() => {
              onApply(draft);
              setAnchor(null);
            }}
          >
            Apply
          </Button>
        </Box>
      </Popover>
    </Box>
  );
}

function SheetFrame({
  title,
  exportName,
  csvText,
  enabled,
  children,
  onClear,
  description,
}: {
  title: string;
  exportName: string;
  csvText: string;
  enabled: boolean;
  children: React.ReactNode;
  onClear?: () => void;
  description?: string;
}) {
  return (
    <Box
      onClick={onClear}
      sx={{
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        minWidth: 0,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        p: 1.5,
      }}
    >
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{title}</Typography>
        {enabled ? (
          <Button
            size="small"
            variant="outlined"
            sx={{ textTransform: 'none', flexShrink: 0 }}
            onClick={(event) => {
              event.stopPropagation();
              download(exportName, csvText);
            }}
          >
            Export CSV
          </Button>
        ) : null}
      </Box>
      {description && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, mb: 1 }}>
          {description}
        </Typography>
      )}
      <Box sx={{ minHeight: 0, maxHeight: '70vh', flex: 1, overflow: 'auto' }}>{children}</Box>
    </Box>
  );
}

function ResultsTable({ rows }: { rows: readonly ResultRow[] }) {
  const [page, setPage] = useState(0);
  const pages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const index = Math.min(page, pages - 1);
  const printing = useNativePrintMode();
  const visible = printing ? rows : rows.slice(index * PAGE_SIZE, index * PAGE_SIZE + PAGE_SIZE);
  const total = resultTotal(rows);
  const totalRow: ResultRow = {
    campaignWeek: '',
    source: '',
    dayName: '',
    campaign: '',
    mktSource: '',
    rank: 0,
    ...total,
  };
  return (
    <Box>
      <Box component="table" sx={{ borderCollapse: 'collapse', width: 'max-content', minWidth: '100%', fontSize: 12 }}>
        <Box component="thead">
          <Box component="tr" sx={{ height: 40 }}>
            {['Campaign Week', 'Source', 'Day', 'Campaign', ...RESULT_MEASURES].map((header) => (
              <Box
                component="th"
                key={header}
                sx={{
                  textAlign: 'left',
                  fontWeight: 700,
                  borderBottom: 1,
                  borderColor: 'divider',
                  px: 0.5,
                  whiteSpace: 'normal',
                  width:
                    header === 'Campaign'
                      ? 324
                      : header === 'Source'
                        ? 210
                        : header === 'Campaign Week'
                          ? 86
                          : undefined,
                }}
              >
                {header}
              </Box>
            ))}
          </Box>
        </Box>
        <Box component="tbody">
          <Box component="tr" sx={{ fontWeight: 700, bgcolor: 'action.hover' }}>
            <Box component="td" sx={{ px: 0.5 }}>
              Grand Total
            </Box>
            <Box component="td" />
            <Box component="td" />
            <Box component="td" />
            {RESULT_MEASURES.map((header) => (
              <Box component="td" key={header} sx={{ px: 0.5 }}>
                {measureText(totalRow, header)}
              </Box>
            ))}
          </Box>
          {visible.map((row, rowIndex) => {
            const previous = visible[rowIndex - 1];
            const week = previous?.campaignWeek === row.campaignWeek ? '' : formatDate(row.campaignWeek);
            const tip = `Campaign Week:\t${formatDate(row.campaignWeek)}\nCampaign:\t${row.campaign}\nSource:\t${row.mktSource}`;
            return (
              <Box component="tr" key={`${row.campaignWeek}-${row.source}-${row.dayName}-${row.campaign}`}>
                <Box component="td" sx={{ px: 0.5 }} title={tip}>
                  {week}
                </Box>
                <Box component="td" sx={{ px: 0.5 }} title={tip}>
                  {row.source === '' ? '(blank)' : row.source}
                </Box>
                <Box component="td" sx={{ px: 0.5 }}>
                  {row.dayName}
                </Box>
                <Box component="td" sx={{ px: 0.5, maxWidth: 324 }}>
                  {row.campaign}
                </Box>
                {RESULT_MEASURES.map((header) => (
                  <Box
                    component="td"
                    key={header}
                    title={`${tip}\n${header}:\t${measureText(row, header)}`}
                    sx={{ px: 0.5 }}
                  >
                    {measureText(row, header)}
                  </Box>
                ))}
              </Box>
            );
          })}
        </Box>
      </Box>
      {pages > 1 && !printing ? (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
          <Button size="small" disabled={index === 0} onClick={() => setPage(index - 1)}>
            Previous
          </Button>
          <Typography sx={{ fontSize: 12 }}>
            {index * PAGE_SIZE + 1}–{Math.min(rows.length, (index + 1) * PAGE_SIZE)} of {rows.length}
          </Typography>
          <Button size="small" disabled={index >= pages - 1} onClick={() => setPage(index + 1)}>
            Next
          </Button>
        </Box>
      ) : null}
    </Box>
  );
}

function WeeklyChart({ rows }: { rows: readonly WeekBar[] }) {
  const theme = useTheme();
  const narrow = useMediaQuery(theme.breakpoints.down('sm'));
  const max = Math.max(1, ...rows.map((row) => row.users));
  const maxCvr = Math.max(0.01, ...rows.flatMap((row) => (row.cvr === null ? [] : [row.cvr])));
  const average = rows.length ? rows.reduce((sum, row) => sum + row.users, 0) / rows.length : 0;
  const step = Math.max(1, Math.ceil(rows.length / (narrow ? 4 : 12)));
  const top = Math.ceil(max / 500) * 500;
  const ticks = [0, 1, 2, 3, 4].map((index) => Math.round((top * index) / 4));
  return (
    <Box
      sx={{
        position: 'relative',
        height: { xs: 300, md: 360 },
        minHeight: 280,
        minWidth: narrow ? Math.max(560, rows.length * 4 + 84) : 0,
        display: 'grid',
        gridTemplateColumns: '16px 52px 1fr',
        columnGap: '4px',
        pr: 1,
      }}
    >
      <Box
        sx={{ position: 'absolute', top: -4, right: 8, display: 'flex', alignItems: 'center', gap: 0.75, fontSize: 10 }}
      >
        CVR · 0.0%
        <Box
          component="span"
          sx={{
            width: 90,
            height: 8,
            opacity: 0.6,
            background: `linear-gradient(to right, ${continuousColor(0, 0, maxCvr)}, ${continuousColor(maxCvr / 2, 0, maxCvr)}, ${continuousColor(maxCvr, 0, maxCvr)})`,
          }}
        />
        {formatPercent(maxCvr)} · Grey = unknown
      </Box>
      <Typography sx={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', fontSize: 11, textAlign: 'center' }}>
        Users
      </Typography>
      <Box sx={{ position: 'relative' }}>
        <Box sx={{ position: 'absolute', top: 16, right: 0, bottom: 28, left: 0 }}>
          {ticks.map((tick) => (
            <Typography
              key={tick}
              sx={{
                position: 'absolute',
                right: 0,
                bottom: `${(tick / top) * 100}%`,
                transform: 'translateY(50%)',
                fontSize: 10,
                lineHeight: 1,
                whiteSpace: 'nowrap',
              }}
            >
              {formatInteger(tick)}
            </Typography>
          ))}
        </Box>
      </Box>
      <Box sx={{ position: 'relative', height: '100%' }}>
        <Box
          sx={{ position: 'absolute', inset: '16px 0 28px 0', borderLeft: 1, borderBottom: 1, borderColor: 'divider' }}
        >
          {rows.length ? (
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: `${(average / top) * 100}%`,
                borderTop: 2,
                borderColor: 'text.secondary',
              }}
            >
              <Typography sx={{ position: 'absolute', top: -16, left: 4, fontSize: 11 }}>Average</Typography>
            </Box>
          ) : null}
          <Box sx={{ display: 'flex', alignItems: 'flex-end', height: '100%', gap: '1px' }}>
            {rows.map((row, index) => (
              <Box
                key={row.campaignWeek}
                title={`Campaign Week: ${formatDate(row.campaignWeek)}\nUsers: ${formatInteger(row.users)}\nConversion Rate: ${formatPercent(row.cvr)}\nTTV: ${formatCurrency(row.ttv)}\nGross Margin: ${formatCurrency(row.grossMargin)}`}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-end',
                  position: 'relative',
                }}
              >
                <Box
                  sx={{
                    height: `${(row.users / top) * 100}%`,
                    bgcolor: row.cvr === null ? 'action.disabled' : continuousColor(row.cvr, 0, maxCvr),
                    opacity: 0.6,
                  }}
                />
                {index % step === 0 ? (
                  <Typography
                    sx={{
                      position: 'absolute',
                      bottom: -24,
                      left: 0,
                      fontSize: 8,
                      transform: 'rotate(-60deg)',
                      transformOrigin: 'top left',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {formatDate(row.campaignWeek)}
                  </Typography>
                ) : null}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function SourceChart({
  rows,
  action,
  onAction,
}: {
  rows: readonly SourceBar[];
  action: string | null;
  onAction: (source: string | null) => void;
}) {
  const max = Math.max(1, ...rows.map((row) => row.users));
  return (
    <Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr) minmax(0, 1fr)', sm: '220px minmax(0, 1fr)' },
          columnGap: 1,
        }}
      >
        <Typography sx={{ fontSize: 12, fontWeight: 700 }}>Source</Typography>
        <Typography sx={{ fontSize: 12, fontWeight: 700 }}>Users · conversion rate</Typography>
        {rows.map((row) => {
          const tip = `${row.source || '(blank)'} · Users: ${formatInteger(row.users)} · Conversion Rate: ${formatPercent(row.cvr)} · TTV: ${formatCurrency(row.ttv)} · Gross Margin: ${formatCurrency(row.grossMargin)}`;
          return (
            <Tooltip key={row.source || '(blank)'} title={tip} arrow>
              <ButtonBase
                aria-label={tip}
                aria-pressed={action === row.source}
                onClick={(event) => {
                  event.stopPropagation();
                  onAction(action === row.source ? null : row.source);
                }}
                sx={{
                  gridColumn: '1 / -1',
                  display: 'grid',
                  gridTemplateColumns: { xs: 'minmax(0, 1fr) minmax(0, 1fr)', sm: '220px minmax(0, 1fr)' },
                  columnGap: 1,
                  textAlign: 'left',
                  borderRadius: 0.5,
                  py: 0.5,
                  px: 0.5,
                  bgcolor: action === row.source ? 'action.selected' : 'transparent',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <Typography sx={{ fontSize: 12, minWidth: 0, overflowWrap: 'anywhere' }}>
                  {row.source || '(blank)'}
                </Typography>
                <Box sx={{ minWidth: 0 }}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    gap={0.5}
                    sx={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    <Typography variant="caption">{formatInteger(row.users)}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatPercent(row.cvr)}
                    </Typography>
                  </Stack>
                  <Box
                    sx={{
                      width: `${(row.users / max) * 100}%`,
                      minWidth: 2,
                      height: 6,
                      borderRadius: 1,
                      bgcolor: (theme) => lensTokens(theme).accent,
                      opacity: action === row.source ? 1 : 0.6,
                    }}
                  />
                </Box>
              </ButtonBase>
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
}

function parseList(value: unknown): string[] {
  return String(value)
    .split(',')
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
}

function QrCampaignsContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const view = params.get('view') === INSIGHTS_VIEW ? INSIGHTS_VIEW : SUMMARY_VIEW;
  const from = readDate(params, 'from');
  const to = readDate(params, 'to');
  const insightsFrom = readDate(params, 'ifrom');
  const insightsTo = readDate(params, 'ito');
  const sources = readSources(params, 'source');
  const campaigns = readMulti(params, 'campaign');
  const countries = readMulti(params, 'country');
  const states = readMulti(params, 'state');
  const insightsSources = readSources(params, 'isource');
  const insightsCampaigns = readMulti(params, 'icampaign');
  const action = params.get('action');
  const summaryScope: SummaryScope = { from, to, sources, campaigns, countries, states };
  const insightsScope: InsightsScope = {
    from: insightsFrom,
    to: insightsTo,
    sources: insightsSources,
    campaigns: insightsCampaigns,
    action,
  };
  const query = useGetReportingLeLensData(nativeLensId(PAGE_ID), {}, false);
  const card = query.data?.cards?.find((item) => item.cardId === CARD_ID);
  const parsed = useMemo(() => readFacts((card?.rows ?? []) as Record<string, unknown>[]), [card]);
  const ready = card?.status === 'ready' || card?.status === 'empty';
  const failed = query.isError || Boolean(card && !ready);
  const failure = query.isError
    ? nativeLensFailure(query.error, 'QR Campaigns')
    : nativeLensFailure(card?.message ? new Error(card.message) : undefined, 'QR Campaigns');
  const exportable = ready && !card?.truncated && !query.isShowingPreviousData;
  const results = useMemo(
    () => weeklyResults(parsed, summaryScope),
    [parsed, from, to, sources, campaigns, countries, states],
  );
  const weeks = useMemo(
    () => weeklyBars(parsed, insightsScope),
    [parsed, insightsFrom, insightsTo, insightsCampaigns, action],
  );
  const sourcesBars = useMemo(() => sourceBars(parsed, insightsScope), [parsed, insightsSources, action]);
  const dates = extent(parsed);
  const summaryCampaignOptions = useMemo(
    () =>
      [...new Set(weeklyResults(parsed, { ...summaryScope, campaigns: 'all' }).map((row) => row.campaign))].sort(
        (a, b) => a.localeCompare(b),
      ),
    [parsed, from, to, sources, countries, states],
  );
  const insightsCampaignOptions = useMemo(
    () =>
      distinctValues(
        parsed,
        'campaign',
        (fact) =>
          (!insightsFrom || fact.campaignDate >= insightsFrom) &&
          (!insightsTo || fact.campaignDate <= insightsTo) &&
          (!action || fact.source === action),
      ),
    [parsed, insightsFrom, insightsTo, action],
  );
  const sourceOptions = useMemo(() => distinctValues(parsed, 'source'), [parsed]);
  const countryOptions = useMemo(() => distinctValues(parsed, 'country'), [parsed]);
  const stateOptions = useMemo(() => distinctValues(parsed, 'state'), [parsed]);
  const summaryFacts = useMemo(
    () =>
      parsed.filter(
        (fact) =>
          fact.showDate &&
          (sources === 'all' || (sources === 'saved' ? SAVED.has(fact.source) : sources.includes(fact.source))),
      ),
    [parsed, sources],
  );
  const replace = (next: URLSearchParams) =>
    navigate({ pathname: location.pathname, search: next.toString() }, { replace: true });
  const update = (changes: Record<string, string | null>) => {
    const next = new URLSearchParams(location.search);
    Object.entries(changes).forEach(([key, value]) => {
      if (value === null || value === '') next.delete(key);
      else next.set(key, value);
    });
    replace(next);
  };
  const contextKeys =
    view === SUMMARY_VIEW
      ? ['view', 'from', 'to', 'source', 'campaign', 'country', 'state']
      : ['view', 'insightsFrom', 'insightsTo', 'insightsSource', 'insightsCampaign', 'action'];
  const contextSchema = Object.fromEntries(Object.entries(SCHEMA).filter(([key]) => contextKeys.includes(key)));
  usePageBridge({
    pageId: PAGE_ID,
    view: {
      title: `QR Campaigns · ${view === SUMMARY_VIEW ? 'Summary' : 'Insights'}`,
      ready: Boolean(ready),
      filterSchema: contextSchema,
      filters: Object.fromEntries(
        Object.entries({
          view,
          from: from ?? '',
          to: to ?? '',
          insightsFrom: insightsFrom ?? '',
          insightsTo: insightsTo ?? '',
          source: sources === 'saved' ? 'saved' : sources === 'all' ? 'all' : sources.join(','),
          campaign: campaigns === 'all' ? 'all' : campaigns.join(','),
          country: countries === 'all' ? 'all' : countries.join(','),
          state: states === 'all' ? 'all' : states.join(','),
          insightsSource:
            insightsSources === 'saved' ? 'saved' : insightsSources === 'all' ? 'all' : insightsSources.join(','),
          insightsCampaign: insightsCampaigns === 'all' ? 'all' : insightsCampaigns.join(','),
          action: action ?? '',
        }).filter(([key]) => contextKeys.includes(key)),
      ),
      dataMode: 'live BigQuery through Lens',
      filterScope:
        view === SUMMARY_VIEW
          ? 'Summary date, source, campaign, country and state filters apply to Weekly Results. Blank dates use the complete available campaign period.'
          : 'Weekly chart: campaign dates and Campaign. Source chart: Source across all campaign dates and campaigns. The selected source bar action applies to both charts. Blank dates use the complete available campaign period.',
      summary: {
        worksheet: view === SUMMARY_VIEW ? 'Weekly Results | By Campaign Week Starting Date' : 'QR Campaign Insights',
        sessions: sessionsSum(view === SUMMARY_VIEW ? results : weeks),
        rows: view === SUMMARY_VIEW ? results.length : weeks.length,
        action,
        complete: exportable,
      },
      definitions: {
        users:
          'users is already COUNT DISTINCT stitched_user_id on each print_qr_campaigns row. Worksheets SUM that column and do not count it again.',
        sessions: 'sessions is already COUNT DISTINCT session_id on each row. Worksheets sum it.',
        campaignWeek: 'Campaign Week is DATE_TRUNC of campaign_date to the week starting Friday, shown as D/M/YYYY.',
        rank: 'Rank is RANK of each Weekly Results row by its SUM(users) inside the Campaign Week. The label is hidden. Ties break by Source, Day, then Campaign.',
        showDate:
          'Weekly Results keeps campaign_date before today in Australia/Sydney, plus the saved Source members. Insights weekly bars do not use that Source set.',
        source:
          'Source is the workbook CASE on ASCII-trimmed mkt_source. The saved filter matches the CASE output, including mapped names and raw values that fall through to ELSE.',
        action:
          'The Source picker filters only the Source chart; date and Campaign pickers filter only the Weekly chart, matching the workbook. Selecting a bar in Campaign Source | By Users and CVR filters both sheets on QR Campaign Insights by that Source. Selecting it again, or the sheet background, clears the filter.',
      },
    },
    edit: (changes) => {
      if (!ready) throw new Error('Page data is still loading.');
      validatePageFilters(changes, contextSchema);
      const next = new URLSearchParams(location.search);
      if ('view' in changes) next.set('view', String(changes.view));
      if ('from' in changes) changes.from ? next.set('from', String(changes.from)) : next.delete('from');
      if ('to' in changes) changes.to ? next.set('to', String(changes.to)) : next.delete('to');
      if ('insightsFrom' in changes)
        changes.insightsFrom ? next.set('ifrom', String(changes.insightsFrom)) : next.delete('ifrom');
      if ('insightsTo' in changes)
        changes.insightsTo ? next.set('ito', String(changes.insightsTo)) : next.delete('ito');
      if ('action' in changes) changes.action ? next.set('action', String(changes.action)) : next.delete('action');
      if ('source' in changes)
        writeSources(
          next,
          'source',
          changes.source === 'saved' || changes.source === 'all' ? changes.source : parseList(changes.source),
        );
      if ('insightsSource' in changes)
        writeSources(
          next,
          'isource',
          changes.insightsSource === 'saved' || changes.insightsSource === 'all'
            ? changes.insightsSource
            : parseList(changes.insightsSource),
        );
      if ('campaign' in changes)
        writeMulti(next, 'campaign', changes.campaign === 'all' ? 'all' : parseList(changes.campaign));
      if ('country' in changes)
        writeMulti(next, 'country', changes.country === 'all' ? 'all' : parseList(changes.country));
      if ('state' in changes) writeMulti(next, 'state', changes.state === 'all' ? 'all' : parseList(changes.state));
      if ('insightsCampaign' in changes)
        writeMulti(next, 'icampaign', changes.insightsCampaign === 'all' ? 'all' : parseList(changes.insightsCampaign));
      replace(next);
    },
  });
  const setSources = (values: string[]) => {
    const next = new URLSearchParams(location.search);
    writeSources(next, 'source', settleSources(values, sourceOptions));
    replace(next);
  };
  const setInsightsSources = (values: string[]) => {
    const next = new URLSearchParams(location.search);
    writeSources(next, 'isource', settleSources(values, sourceOptions));
    replace(next);
  };
  return (
    <Box
      sx={{
        width: '100%',
        minWidth: 0,
        p: { xs: 1, sm: 2 },
        fontFamily: FONT_STACK_SUISSE_INTL,
        '& table, & th, & td': { fontFamily: 'inherit' },
      }}
    >
      <Box
        data-testid="qr-canvas"
        sx={{
          width: '100%',
          minWidth: 0,
          bgcolor: 'background.default',
          color: 'text.primary',
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1fr) 250px' },
          gap: 2,
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', sm: 'center' }}
          gap={1}
          sx={{ gridColumn: '1 / -1', gridRow: 1 }}
        >
          <Box>
            <Typography component="h1" sx={{ fontFamily: FONT_STACK_SUISSE_INTL, fontSize: 23, fontWeight: 600 }}>
              QR Campaigns
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 1 }}>
              {VIEWS.map((item) => (
                <Button
                  key={item}
                  variant={view === item ? 'contained' : 'text'}
                  aria-pressed={view === item}
                  onClick={() => update({ view: item === SUMMARY_VIEW ? null : item })}
                  sx={{ textTransform: 'none', fontSize: 13 }}
                >
                  {item}
                </Button>
              ))}
            </Stack>
          </Box>
          <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center">
            <PageChatButton />
            <Button
              onClick={() => replace(resetViewFilters(params, view))}
              sx={{ alignSelf: { xs: 'flex-start', sm: 'center' }, flexShrink: 0 }}
            >
              Reset filters
            </Button>
          </Stack>
        </Stack>
        {!ready && !failed ? (
          <Box sx={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <CircularProgress />
          </Box>
        ) : null}
        {failed ? (
          <Box sx={{ gridColumn: '1 / -1', p: 2 }}>
            <Alert
              severity={failure.severity}
              action={
                failure.retryable ? (
                  <Button color="inherit" onClick={() => void query.refetch()}>
                    Retry
                  </Button>
                ) : undefined
              }
            >
              {failure.message}
            </Alert>
          </Box>
        ) : null}
        {ready && view === SUMMARY_VIEW ? (
          <>
            <Box sx={{ gridColumn: 1, gridRow: { xs: 3, lg: 2 }, minWidth: 0 }}>
              <SheetFrame
                description="Campaign date, Source, Campaign, Country and State apply to this table. Reset restores the saved workbook defaults."
                title="Weekly Results | By Campaign Week Starting Date"
                exportName="qr-campaigns-weekly-results.csv"
                csvText={resultCsv(results)}
                enabled={exportable}
              >
                {card?.truncated ? (
                  <Typography sx={{ fontSize: 12 }}>
                    This extract stopped early, so the sheet is not complete.
                  </Typography>
                ) : null}
                <ResultsTable rows={results} />
              </SheetFrame>
            </Box>
            <Box
              sx={{
                gridColumn: { xs: 1, lg: 2 },
                gridRow: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                minWidth: 0,
                bgcolor: 'background.paper',
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                p: 1.5,
                alignSelf: 'start',
              }}
            >
              <DateRange
                from={from}
                to={to}
                min={dates.min}
                max={dates.max}
                facts={summaryFacts}
                onChange={(nextFrom, nextTo) => {
                  const next = new URLSearchParams(location.search);
                  if (nextFrom && nextFrom !== dates.min) next.set('from', nextFrom);
                  else next.delete('from');
                  if (nextTo && nextTo !== dates.max) next.set('to', nextTo);
                  else next.delete('to');
                  replace(next);
                }}
              />
              <CheckFilter
                label="Source"
                options={sourceOptions}
                checked={checkedOf(sources, sourceOptions)}
                onApply={setSources}
              />
              <CheckFilter
                label="Campaign"
                options={summaryCampaignOptions}
                checked={checkedOf(campaigns, summaryCampaignOptions)}
                onApply={(values) => {
                  const next = new URLSearchParams(location.search);
                  writeMulti(next, 'campaign', settleMulti(values, summaryCampaignOptions));
                  replace(next);
                }}
              />
              <CheckFilter
                label="Country"
                options={countryOptions}
                checked={checkedOf(countries, countryOptions)}
                onApply={(values) => {
                  const next = new URLSearchParams(location.search);
                  writeMulti(next, 'country', settleMulti(values, countryOptions));
                  replace(next);
                }}
              />
              <CheckFilter
                label="State"
                options={stateOptions}
                checked={checkedOf(states, stateOptions)}
                onApply={(values) => {
                  const next = new URLSearchParams(location.search);
                  writeMulti(next, 'state', settleMulti(values, stateOptions));
                  replace(next);
                }}
              />
            </Box>
          </>
        ) : null}
        {ready && view === INSIGHTS_VIEW ? (
          <>
            <Box sx={{ gridColumn: 1, gridRow: { xs: 3, lg: 2 }, display: 'grid', gap: 2, minWidth: 0 }}>
              <SheetFrame
                description="Weekly chart: campaign date and Campaign filters. Includes all sources unless you select a source bar below."
                title="Weekly QR Campaigns | By Users and CVR"
                exportName="qr-campaigns-weekly-bars.csv"
                csvText={weekCsv(weeks)}
                enabled={exportable}
              >
                <WeeklyChart rows={weeks} />
              </SheetFrame>
              <SheetFrame
                description="Source chart: Source filter only, across all campaign dates and campaigns. Select a bar to apply that source to both charts."
                title="Campaign Source | By Users and CVR"
                exportName="qr-campaigns-source-bars.csv"
                csvText={sourceCsv(sourcesBars)}
                enabled={exportable}
                onClear={() => action && update({ action: null })}
              >
                <SourceChart rows={sourcesBars} action={action} onAction={(source) => update({ action: source })} />
              </SheetFrame>
            </Box>
            <Box
              sx={{
                gridColumn: { xs: 1, lg: 2 },
                gridRow: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                minWidth: 0,
                bgcolor: 'background.paper',
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                p: 1.5,
                alignSelf: 'start',
              }}
            >
              <Typography variant="subtitle2">Weekly chart filters</Typography>
              <Typography variant="caption" color="text.secondary">
                Apply to the upper weekly chart only.
              </Typography>
              <DateRange
                from={insightsFrom}
                to={insightsTo}
                min={dates.min}
                max={dates.max}
                facts={parsed}
                onChange={(nextFrom, nextTo) => {
                  const next = new URLSearchParams(location.search);
                  if (nextFrom && nextFrom !== dates.min) next.set('ifrom', nextFrom);
                  else next.delete('ifrom');
                  if (nextTo && nextTo !== dates.max) next.set('ito', nextTo);
                  else next.delete('ito');
                  replace(next);
                }}
              />
              <CheckFilter
                label="Campaign"
                options={insightsCampaignOptions}
                checked={checkedOf(insightsCampaigns, insightsCampaignOptions)}
                onApply={(values) => {
                  const next = new URLSearchParams(location.search);
                  writeMulti(next, 'icampaign', settleMulti(values, insightsCampaignOptions));
                  replace(next);
                }}
              />
              <Divider />
              <Typography variant="subtitle2">Source chart filter</Typography>
              <Typography variant="caption" color="text.secondary">
                All campaign dates and campaigns. The Source picker applies to the lower chart only.
              </Typography>
              <CheckFilter
                label="Source"
                options={sourceOptions}
                checked={checkedOf(insightsSources, sourceOptions)}
                onApply={setInsightsSources}
              />
              <Divider />
              <Typography variant="caption" color="text.secondary">
                Select a source bar to filter both charts. Their date and campaign scopes remain different.
              </Typography>
              {action && (
                <Chip
                  label={`Both charts: ${action || '(blank)'}`}
                  onDelete={() => update({ action: null })}
                  sx={{ height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 } }}
                />
              )}
            </Box>
          </>
        ) : null}
      </Box>
    </Box>
  );
}

export default function QrCampaignsPage({ printTarget }: NativePrintProps) {
  return (
    <NativeReportShell
      pageId="qr-campaigns"
      title="QR Campaigns"
      printTarget={printTarget}
      allowScheduling={false}
      analytics
    >
      <QrCampaignsContent />
    </NativeReportShell>
  );
}
